import React, { useEffect, useCallback } from "react";
import styled from "styled-components";

// Reduced constants for better performance
const STAR_COUNT = 300; // Reduced from 1200
// eslint-disable-next-line no-unused-vars
const SHOOTING_STAR_COUNT = 3; // Reduced from 8
const SHOOTING_STAR_INTERVAL = 5000; // Less frequent

// Styled Components
const StarContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  overflow: hidden;
  z-index: 0;
  pointer-events: none;
`;

const StarCanvas = styled.canvas`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`;

// Use Canvas API for better performance instead of DOM elements
const StarField = ({
  cameraZ = 0,
  intensity = 0.1,
  performanceMode = false,
}) => {
  const canvasRef = React.useRef(null);
  const animationFrameRef = React.useRef(null);
  const starsRef = React.useRef([]);
  const shootingStarsRef = React.useRef([]);

  // Generate stars once and reuse
  const initializeStars = useCallback(() => {
    const stars = [];
    const starCount = performanceMode ? STAR_COUNT / 2 : STAR_COUNT;
    for (let i = 0; i < starCount; i++) {
      const centerX = 50;
      const centerY = 50;
      const angle = Math.random() * 2 * Math.PI;
      const distance = Math.random() * 70 + 5;

      stars.push({
        id: i,
        originalX: centerX + Math.cos(angle) * distance,
        originalY: centerY + Math.sin(angle) * distance,
        x: centerX + Math.cos(angle) * distance,
        y: centerY + Math.sin(angle) * distance,
        size:
          Math.random() < 0.15
            ? Math.random() * 3 + 1.5
            : Math.random() * 2 + 0.5,
        baseIntensity: Math.random() * 0.6 + 0.4,
        twinklePhase: Math.random() * Math.PI * 2,
        twinkleSpeed: Math.random() * 0.02 + 0.01,
        depth: Math.random() * 4 + 1,
        angle: angle,
      });
    }
    starsRef.current = stars;
  }, [performanceMode]);

  // Optimized star position calculation
  const getStarPosition = useCallback(
    star => {
      if (cameraZ === 0) {
        return { x: star.originalX, y: star.originalY };
      }

      const warpIntensity = (cameraZ / 300) * (star.depth / 2) * intensity;
      const centerX = 50;
      const centerY = 50;

      const deltaX = star.originalX - centerX;
      const deltaY = star.originalY - centerY;

      const accelerationFactor = Math.min(2, warpIntensity * 0.1);
      const warpedX =
        star.originalX + deltaX * warpIntensity * 0.1 * accelerationFactor;
      const warpedY =
        star.originalY + deltaY * warpIntensity * 0.1 * accelerationFactor;

      return { x: warpedX, y: warpedY };
    },
    [cameraZ, intensity]
  );

  // Optimized star effects calculation
  const getStarEffects = useCallback(
    (star, time) => {
      const baseOpacity = star.baseIntensity * 0.3;

      if (cameraZ === 0) {
        // Simple twinkling when stationary
        const twinkle =
          0.7 + 0.3 * Math.sin(time * star.twinkleSpeed + star.twinklePhase);
        return {
          opacity: baseOpacity * twinkle,
          size: star.size,
        };
      }

      // Enhanced effects during movement
      const movementOpacity = Math.min(
        1.2,
        (cameraZ / 2000) * star.depth * intensity
      );
      const finalOpacity = Math.min(1, baseOpacity + movementOpacity);
      const warpScale =
        1 + Math.min(0.3, (cameraZ / 5000) * star.depth * intensity);

      return {
        opacity: finalOpacity,
        size: star.size * warpScale,
      };
    },
    [cameraZ, intensity]
  );

  // Canvas rendering function
  const render = useCallback(
    time => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext("2d");
      const rect = canvas.getBoundingClientRect();

      // Set canvas size if needed
      if (canvas.width !== rect.width || canvas.height !== rect.height) {
        canvas.width = rect.width;
        canvas.height = rect.height;
      }

      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Render stars
      starsRef.current.forEach(star => {
        const position = getStarPosition(star);
        const effects = getStarEffects(star, time * 0.001);

        // Skip stars that are off-screen
        if (
          position.x < -10 ||
          position.x > 110 ||
          position.y < -10 ||
          position.y > 110
        ) {
          return;
        }

        const x = (position.x / 100) * canvas.width;
        const y = (position.y / 100) * canvas.height;

        // Draw star with glow effect
        ctx.save();
        ctx.globalAlpha = effects.opacity;

        // Outer glow
        ctx.beginPath();
        ctx.arc(x, y, effects.size * 2, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 255, 255, ${effects.opacity * 0.3})`;
        ctx.fill();

        // Inner star
        ctx.beginPath();
        ctx.arc(x, y, effects.size, 0, Math.PI * 2);
        ctx.fillStyle = "white";
        ctx.fill();

        ctx.restore();
      });

      // Render shooting stars (skip in performance mode)
      if (!performanceMode) {
        shootingStarsRef.current.forEach(shootingStar => {
          if (!shootingStar.active) return;

          const progress =
            (time - shootingStar.startTime) / shootingStar.duration;
          if (progress >= 1) {
            shootingStar.active = false;
            return;
          }

          const x =
            shootingStar.startX +
            (shootingStar.endX - shootingStar.startX) * progress;
          const y =
            shootingStar.startY +
            (shootingStar.endY - shootingStar.startY) * progress;

          // Fade in and out
          const opacity =
            progress < 0.2
              ? progress / 0.2
              : progress > 0.8
                ? (1 - progress) / 0.2
                : 1;

          ctx.save();
          ctx.globalAlpha = opacity;
          ctx.translate(x, y);
          ctx.rotate(shootingStar.angle);

          // Draw shooting star trail (trail points opposite to movement direction)
          const gradient = ctx.createLinearGradient(
            0,
            0,
            -shootingStar.length,
            0
          );
          gradient.addColorStop(0, "rgba(255, 255, 255, 1)");
          gradient.addColorStop(0.3, "rgba(255, 255, 255, 0.8)");
          gradient.addColorStop(0.7, "rgba(255, 255, 255, 0.4)");
          gradient.addColorStop(1, "rgba(255, 255, 255, 0)");

          ctx.fillStyle = gradient;
          ctx.fillRect(
            -shootingStar.length,
            -shootingStar.width / 2,
            shootingStar.length,
            shootingStar.width
          );

          // Draw bright head of shooting star
          ctx.fillStyle = "rgba(255, 255, 255, 1)";
          ctx.beginPath();
          ctx.arc(0, 0, shootingStar.width, 0, Math.PI * 2);
          ctx.fill();

          ctx.restore();
        });
      }

      // Continue animation
      animationFrameRef.current = requestAnimationFrame(render);
    },
    [getStarPosition, getStarEffects, performanceMode]
  );

  // Initialize and start animation
  useEffect(() => {
    initializeStars();
    animationFrameRef.current = requestAnimationFrame(render);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [initializeStars, render, performanceMode]);

  // Handle shooting stars (disabled in performance mode)
  useEffect(() => {
    if (performanceMode) return;

    const interval = setInterval(() => {
      const startX = Math.random() * window.innerWidth;
      const startY = -50; // Start above screen

      // Create steeper angle (45-70 degrees downward)
      const steepAngle = Math.PI / 4 + Math.random() * (Math.PI / 6); // 45-75 degrees
      const distance = Math.sqrt(
        window.innerWidth * window.innerWidth +
          window.innerHeight * window.innerHeight
      );

      const endX = startX + Math.cos(steepAngle) * distance * 0.6; // Diagonal movement
      const endY = window.innerHeight + 50; // End below screen

      // Calculate angle for proper physics (steeper top-to-bottom direction)
      const deltaX = endX - startX;
      const deltaY = endY - startY;
      const angle = Math.atan2(deltaY, deltaX);

      const newShootingStar = {
        id: Date.now(),
        startX,
        startY,
        endX,
        endY,
        angle, // Proper physics angle
        length: 100 + Math.random() * 100,
        width: 2 + Math.random() * 2,
        duration: 1000 + Math.random() * 1000,
        startTime: performance.now(),
        active: true,
      };

      // Keep only active shooting stars
      shootingStarsRef.current = [
        ...shootingStarsRef.current.filter(s => s.active).slice(-2),
        newShootingStar,
      ];
    }, SHOOTING_STAR_INTERVAL);

    return () => clearInterval(interval);
  }, [performanceMode]);

  return (
    <StarContainer>
      <StarCanvas ref={canvasRef} />
    </StarContainer>
  );
};

export default StarField;
